<template>
  <section
    ref="container"
    class="h-full min-h-0"
  >
    <virtual-scroll
      class="h-full min-h-0 overflow-auto py-[2px]"
      field="id"
      :items="contactsAlphabetically"
      :item-height="64"
      :padding="1"
      :buffer-size="bufferSize"
      :loading="isLoading"
      @end-reached="emitScroll()"
    >
      <template #item="{ item }">
        <contact-list-item-letter
          v-if="typeof item.value === 'string'"
          :letter="item.value"
        />
        <selected-contacts-list
          v-else-if="item.id === 'selectedContactsValues' && 'contacts' in item.value"
          class="px-2 pt-2"
          :contacts="item.value.contacts"
          @remove="removeFromSelected"
        />
        <router-link
          v-else-if="'imLogin' in item.value && item.value.imLogin"
          v-slot="{ isActive, navigate }"
          custom
          :to="{ name: 'Chat', params: { id: getChatRouteId(item.value) } }"
        >
          <div
            class="relative w-full px-2"
            @click.right.prevent="onContactRightClick($event, item.value)"
          >
            <contact-list-item
              :dark-theme="darkTheme"
              :is-selected="isActive"
              is-visible
              :contact="item.value"
              :show-favorite-button="showFavoriteButton"
              @click.right.shift.stop
              @click="withNavigation ? navigate() : emitGetContact(item.value)"
            />
          </div>
        </router-link>
        <div
          v-else
          class="relative w-full px-2"
        >
          <contact-list-item
            :contact="item.value as unknown as Contact"
            :dark-theme="darkTheme"
            :show-favorite-button="showFavoriteButton"
            is-visible
            @click="onExternalContactClick(item.value as unknown as Contact)"
            @click.right.prevent="onContactRightClick($event, item.value as unknown as Contact)"
          />
        </div>
      </template>
    </virtual-scroll>
    <div
      v-if="activeContact && isForAddingToChat"
      ref="contextMenu"
      v-click-outside="hideContextMenu"
      class="absolute z-20 w-min"
    >
      <create-chat-contact-context-menu
        :can-add-as-moderator="!!isModerated && !!activeContact?.imLogin"
        @hide-menu="hideContextMenu"
        @add-as-moderator="$emit('onAddModerator', activeContact)"
        @remove-from-moderators="$emit('onRemoveModerator', activeContact.id)"
        @add-as-user="$emit('onAddSubscriber', activeContact)"
        @remove-from-users="$emit('onRemoveSubscriber', activeContact.id)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { useDialog, ClickOutside as vClickOutside, WuiVirtualScroll } from '@wui/common-library'
import { computed, ComputedRef, PropType, onMounted, nextTick, ref, watch, toRefs, Component } from 'vue'
import { useRouter } from 'vue-router'

import { CreateChatContactContextMenu } from '@/features/create-chat-contact-context-menu'

import { ExternalContactCard } from '@/entities/contact'



import { useLocalization } from '@/shared/composables/localization'
import { usePositionPopUp } from '@/shared/composables/use-position-popup'
import { Contact } from '@/shared/types/api'
import { optionalPush } from '@/shared/utils/router'
import { getChatRoute, getChatRouteId } from '@/shared/utils/url'

import ContactListItem from '../contact-list-item/contact-list-item.vue'
import ContactListItemLetter from '../contact-list-item-letter/contact-list-item-letter.vue'
import SelectedContactsList from '../selected-contacts/selected-contacts-list.vue'

import { SelectedContacts, IContactListItem } from './types'

const props = defineProps({
  contacts: {
    type: Array as PropType<Contact[]>,
    required: true,
  },
  selectedContacts: {
    type: Array as PropType<Contact[]>,
    default: () => [],
  },
  withNavigation: {
    type: Boolean,
    default: true,
  },
  needPushRoute: {
    type: Boolean,
    default: false,
  },
  showFavoriteButton: {
    type: Boolean,
    default: true,
  },
  darkTheme: {
    type: Boolean,
    default: false,
  },
  showExternalContactModal: {
    type: Boolean,
    default: true,
  },
  isModerated: {
    type: Boolean,
  },
  isForAddingToChat: {
    type: Boolean,
  },
  favoritesFirst: {
    type: Boolean,
    default: false,
  },
  total: {
    type: Number,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const VirtualScroll = WuiVirtualScroll as Component

const emit = defineEmits<{
  (event: 'getContact', value: Contact): void;
  (event: 'removeFromSelected', value: string): void;
  (event: 'contactRightClick', e: MouseEvent | TouchEvent, value: Contact): void;
  (event: 'onAddModerator', value: Contact): void
  (event: 'onRemoveModerator', value: string): void
  (event: 'onAddSubscriber', value: Contact): void
  (event: 'onRemoveSubscriber', value: string): void
  (event: 'scroll'): void;
}>()
const activeContact = ref<Contact>()

const container = ref<HTMLElement | null>(null)
const contextMenu = ref<HTMLElement | null>(null)

const emitGetContact = function (contact: Contact) {
  emit('getContact', contact)
}

const { showDialog, closeDialog } = useDialog()
const { t } = useLocalization()

const openedExternalContactId = ref<string | null>(null)
const { contacts, total } = toRefs(props)
const showExternalContactCard = async (contact: Contact) => {
  openedExternalContactId.value = contact.id
  const isClosed = await showDialog(ExternalContactCard, { contactId: contact.id, hasOverlay: true })
  if (isClosed) {
    openedExternalContactId.value = null
  }
}

watch(() => contacts.value.find(contact => contact.id === openedExternalContactId.value), (contact, contactOld) => {
  if (!contactOld) {
    return
  }

  if (contact) {
    if (closeDialog.value) {
      closeDialog.value(undefined)
    }
    showExternalContactCard(contact)
  }
}, { deep: true })

const SELECTED_CONTACTS = t('mainAside.addedToChat')
const contactsAlphabetically: ComputedRef<(IContactListItem<Contact | string | SelectedContacts>)[]> = computed(() => {
  const acc: IContactListItem<Contact | string | SelectedContacts>[] = []
  if (props.selectedContacts && props.selectedContacts.length) {
    acc.push({
      id: SELECTED_CONTACTS,
      value: SELECTED_CONTACTS,
    })
    const selectedContacts: SelectedContacts = {
      name: SELECTED_CONTACTS,
      contacts: props.selectedContacts,
    }
    acc.push({
      id: 'selectedContactsValues',
      value: selectedContacts,
    })
  }

  return contacts.value.reduce((acc, contact, index) => {
    const firstChar = contact.isFavorite ? '★' : contact.name.charAt(0)
    const prevContact = index > 0 ? contacts.value[index - 1] : undefined
    const prevFirstChar = prevContact
      ? (prevContact.isFavorite ? '★' : prevContact.name.charAt(0))
      : undefined
    const letterChanged = prevFirstChar === undefined || prevFirstChar !== firstChar
    const shouldAddLetter = props.favoritesFirst
      ? letterChanged
      : !acc.find(item => item.value === firstChar)

    if (shouldAddLetter) {
      acc.push({
        id: props.favoritesFirst
          ? `letter-${contact.isFavorite ? 'fav' : 'reg'}-${firstChar}-${index}`
          : firstChar,
        value: firstChar,
      })
    }

    acc.push({
      id: contact.id,
      value: contact,
    })

    return acc
  }, acc)
})

const bufferSize = computed(() => {
  return contactsAlphabetically.value.length > total.value ? contactsAlphabetically.value.length : total.value
})

const emitScroll = () => {
  emit('scroll')
}

const removeFromSelected = (id: string) => {
  emit('removeFromSelected', id)
}

const onContactRightClick = async (event: MouseEvent | TouchEvent, contact: Contact) => {
  emit('contactRightClick', event, contact)

  activeContact.value = contact
  await nextTick()
  usePositionPopUp({
    event,
    container,
    popup: contextMenu,
  })
}

const hideContextMenu = () => {
  activeContact.value = undefined
}

const onExternalContactClick = (contact: Contact) => {
  if (props.showExternalContactModal) {
    showExternalContactCard(contact)
  } else {
    emitGetContact(contact)
  }
}

onMounted(() => {
  const { currentRoute } = useRouter()
  if (currentRoute.value.name === 'Chat' || !props.needPushRoute) {
    return
  }

  const contactItem = contactsAlphabetically.value.find((item) => {
    if (typeof item.value === 'string') {
      return false
    }
    const { imLogin } = item.value as Contact
    return !!imLogin
  }) as IContactListItem<Contact>

  optionalPush(contactItem?.value ? getChatRoute(contactItem.value) : undefined)
})
</script>

